import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoalsPanel from '../components/GoalsPanel';
import QueueItemActions from '../components/QueueItemActions';

// The Dashboard — what an agent lands on when they sign in, and the answer to "what do I do today?".
//
// Not a metrics page. The hero is ONE ranked list of everything that wants a person today, soonest
// first, with the reason stated in the row rather than implied by which box it sits in. KPIs are a
// thin strip above it; the calendar, the money and the market alerts sit to the side.
//
// Nothing on this page touches a DEAL's data or sends. The writes it does make are the person's own:
// their goals (routes/goals.js), and what they did about a queue item — complete, snooze, clear
// (routes/queue.js) — plus a note added to a deal from its queue row, through the deal's own notes.
//
// Everything comes from one call (GET /api/dashboard). The page is read at a glance first thing in
// the morning; a dozen parallel requests would have it painting in pieces for several seconds.
//
// The org owner gets a Me / Office toggle. It defaults to Me for them too: the owner of a wholesale
// shop works their own deals alongside their agents, so their home screen is the same personal board
// an agent gets, and Office is the deliberate switch.

const PERIODS = [
  { key: 'current-month', label: 'This month' },
  { key: 'last-month',    label: 'Last month' },
  { key: 'ytd',           label: 'Year to date' },
  { key: 'all',           label: 'All time' },
];

const money = (n) => (n == null ? '—'
  : n >= 1000000 ? `$${(n / 1000000).toFixed(2)}M`
  : n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Calendar dates arrive as plain 'YYYY-MM-DD' — the API formats date-only columns as text precisely
// so that no timezone is involved (see DATE_ONLY in routes/dashboard.js). Passing such a string
// through `new Date()` is what breaks it: the string is read as UTC midnight, and local getters then
// report the PREVIOUS day everywhere west of Greenwich — i.e. for every user, on a UTC server.
//
// So a calendar day is returned untouched and only a genuine Date/timestamp takes the local-getter
// path. Both branches have to exist: `date` is a calendar day, `received_at` is an instant.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function ymd(v) {
  if (!v) return null;
  if (typeof v === 'string') {
    if (DATE_ONLY_RE.test(v)) return v;                 // already a calendar day — do NOT parse it
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v.slice(0, 10) : localYmd(d);
  }
  return v instanceof Date ? localYmd(v) : String(v).slice(0, 10);
}
// Split a calendar day into parts WITHOUT building a Date from the whole string: `new Date('2026-09-21')`
// is UTC midnight, while new Date(2026, 8, 21) is local. Reading the parts avoids the question entirely.
function dayParts(v) {
  const key = ymd(v);
  if (!key) return null;
  const [y, m, d] = key.split('-').map(Number);
  return { y, m, d, short: MONTHS[m - 1] ? MONTHS[m - 1].slice(0, 3) : '' };
}

const SEV = {
  crit: { stripe: 'var(--red)',  when: 'var(--red)' },
  warn: { stripe: 'var(--gold)', when: 'var(--gold)' },
  info: { stripe: 'var(--neutral, #C9C3B2)', when: 'var(--gray-500)' },
};

export default function Dashboard() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('mine');
  const [period, setPeriod] = useState('current-month');
  // Bumped when the goals panel writes, so the page re-reads its own numbers rather than
  // the panel keeping a second, diverging copy of what was just saved.
  const [bump, setBump] = useState(0);
  // Queue keys acted on in this visit. route() answers BEFORE it commits, so the re-read straight after
  // an action can still carry the row; hiding it here as well means it never flickers back.
  const [gone, setGone] = useState(() => new Set());

  useEffect(() => {
    let dead = false;
    setLoading(true);
    fetch(`/api/dashboard?period=${period}&scope=${scope}`)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (dead) return;
        if (!ok) { setError(d.error || 'Could not load your dashboard.'); setLoading(false); return; }
        setData(d); setError(null); setLoading(false);
      })
      .catch(() => { if (!dead) { setError('Could not load your dashboard.'); setLoading(false); } });
    return () => { dead = true; };
  }, [period, scope, bump]);

  const now = new Date();
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || null;
  const office = scope === 'all';
  const canToggle = data?.can_view_org_wide ?? (role === 'owner' || role === 'admin');

  if (loading && !data) return <Centered>Loading your day…</Centered>;
  if (error && !data) return <Centered tone="bad">{error}</Centered>;

  const k = data.kpis;
  const queue = (data.queue || []).filter((q) => !(q.keys || []).length || !(q.keys || []).every((k) => gone.has(k.key)));
  const crit = queue.filter(q => q.severity === 'crit').length;

  return (
    <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 23, fontWeight: 700, letterSpacing: '-0.025em', margin: '0 0 3px' }}>
            {greeting(now)}{firstName ? `, ${firstName}` : ''}.{' '}
            <span style={{ color: 'var(--teal)' }}>
              {DAYS[now.getDay()]}, {MONTHS[now.getMonth()]} {now.getDate()}.
            </span>
          </h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 13, margin: 0 }}>
            {lede(queue, data.messages, office)}
          </p>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {canToggle && (
            <div role="group" aria-label="Whose numbers to show" style={seg}>
              <SegBtn on={!office} onClick={() => setScope('mine')}>My production</SegBtn>
              <SegBtn on={office} onClick={() => setScope('all')}>Office</SegBtn>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PERIODS.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)} aria-pressed={period === p.key}
                style={{
                  ...pill,
                  background: period === p.key ? 'var(--teal)' : 'transparent',
                  borderColor: period === p.key ? 'var(--teal)' : 'var(--navy-border)',
                  color: period === p.key ? '#fff' : 'var(--gray-500)',
                }}>{p.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={kpiGrid}>
        <Kpi label="Offers sent" value={k.offers_sent} sub={PERIODS.find(p => p.key === period)?.label} />
        <Kpi label="Under contract" value={k.under_contract} sub="Right now" />
        {/* Sold, escrow not yet funded. Sits between Under contract and Units closed because that is
            where it sits in the deal's life — and it is the bucket that needs chasing, so it earns a
            place on the strip more than a reply rate did. */}
        <Kpi label="Pending" value={k.pending}
          sub={k.pending ? `${money(k.pending_fees)} awaiting close` : 'Nothing awaiting close'} />
        <Kpi label="Units closed" value={k.units} sub={office ? 'Office total' : 'Your credits'} />
        <Kpi label="Fees earned" value={money(k.fees)} sub={office ? 'Office total' : 'Your credits'} feature />
      </div>

      {/* Board */}
      <div style={board}>
        <div style={col}>

          <Panel title="Needs you today"
            count={queue.length} hot={crit > 0}
            link={queue.length ? 'Open Deals →' : null} onLink={() => navigate('/deals')}>
            {queue.length === 0 ? (
              <Empty>
                Nothing needs you right now.
                <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 6 }}>
                  Deadlines, replies and stalled offers will appear here as they come up.
                </div>
              </Empty>
            ) : queue.map((q, i) => (
              <QueueRow key={q.keys?.[0]?.key || `${q.kind}-${q.deal_id || i}`} item={q} queue={queue}
                tz={data.market_timezone}
                onDone={(done) => { if (done?.length) setGone(g => new Set([...g, ...done])); setBump(b => b + 1); }}
                onOpen={() => q.deal_id ? navigate(`/deals/${q.deal_id}`) : navigate('/deals')} />
            ))}
            <Hint>Complete, snooze or clear only changes your own list. A note is saved to the deal.</Hint>
          </Panel>

          <Panel title="New messages" count={data.messages.length} hot={data.messages.length > 0}
            link="All communications →" onLink={() => navigate('/communications')}>
            {data.messages.length === 0
              ? <Empty>No new replies.</Empty>
              : data.messages.map(m => (
                <button key={m.id} onClick={() => m.deal_id && navigate(`/deals/${m.deal_id}`)} style={msgRow}>
                  <span style={avatar}>{initials(m.from)}</span>
                  <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{m.from || 'Unknown sender'}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--gray-300)' }}>
                      {m.subject || m.address || '(no subject)'}
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.snippet}
                    </span>
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{ago(m.received_at)}</span>
                </button>
              ))}
          </Panel>

          <Panel title="Pipeline" count={office ? 'Office' : 'Yours'}
            link="Open Deals →" onLink={() => navigate('/deals')}>
            <Funnel rows={data.funnel} onPick={() => navigate('/deals')} />
          </Panel>
        </div>

        <div style={col}>
          {data.goals && (
            <Panel title="Goals"
              count={data.goals.prompt ? 'Set' : data.goals.year_nudge === 'set' ? 'Set'
                     : data.goals.year_nudge === 'review' ? 'Check in' : null}
              hot={Boolean(data.goals.prompt || data.goals.year_nudge)}>
              <GoalsPanel goals={data.goals} office={office} onSaved={() => setBump(b => b + 1)} />
            </Panel>
          )}

          <Panel title="Calendar">
            <Calendar events={data.calendar} onPick={(id) => navigate(`/deals/${id}`)} />
          </Panel>

          <Panel title="Earnest money"
            count={data.earnest_money.out.count || null}
            link="Open Deals →" onLink={() => navigate('/deals')}>
            <EarnestMoney emd={data.earnest_money} onPick={(id) => navigate(`/deals/${id}`)} office={office} />
          </Panel>

          {(data.alerts.saved_search_unseen > 0 || data.alerts.deal_price_drops.length > 0) && (
            <Panel title="Market moves"
              count={data.alerts.saved_search_unseen || null}
              hot={data.alerts.saved_search_unseen > 0}
              link="MLS Opportunities →" onLink={() => navigate('/mls-opportunities')}>
              {data.alerts.saved_search_hits.map(h => (
                <div key={h.id} style={alertRow}>
                  <span style={{ ...tag, background: h.reason === 'new' ? 'var(--teal-dim)' : 'rgba(184,84,80,0.12)',
                                 color: h.reason === 'new' ? 'var(--teal)' : 'var(--red)' }}>
                    {h.reason === 'new' ? 'New' : h.newly_matching ? 'In range' : 'Price cut'}
                  </span>
                  <span style={alertAddr}>{h.address}</span>
                  <span style={alertPrice}>
                    {h.reason === 'price_drop'
                      ? <><s style={{ color: 'var(--gray-500)' }}>{money(h.old_price)}</s> {money(h.new_price)}</>
                      : money(h.new_price ?? h.list_price)}
                  </span>
                </div>
              ))}
              {data.alerts.deal_price_drops.map(d => (
                <button key={d.event_id} onClick={() => navigate(`/deals/${d.deal_id}`)} style={{ ...alertRow, width: '100%', border: 0, background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <span style={{ ...tag, background: 'var(--gold-dim)', color: '#8a6314' }}>Your deal</span>
                  <span style={alertAddr}>{d.address}</span>
                  <span style={alertPrice}>−{money(d.drop_amount)}{d.drop_pct ? ` (${d.drop_pct}%)` : ''}</span>
                </button>
              ))}
            </Panel>
          )}

          {data.board.unclaimed > 0 && (
            <Panel title="Available to claim" count={data.board.unclaimed}
              link="Pipeline →" onLink={() => navigate('/pipeline')}>
              {data.board.rows.map(r => (
                <div key={r.id} style={claimRow}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{r.address}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
                      {[r.beds && `${r.beds}bd`, r.baths && `${r.baths}ba`,
                        r.sqft && `${Number(r.sqft).toLocaleString()} sqft`].filter(Boolean).join(' · ')}
                      {r.price ? ` · ${money(Number(r.price))}` : ''}
                    </div>
                  </div>
                </div>
              ))}
              {data.board.added_today > 0 && <Hint>{data.board.added_today} added in the last day.</Hint>}
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────────
function greeting(d) {
  const h = d.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

// The one line under the greeting. It states the shape of the day rather than repeating the counts
// that are already on screen — "nothing needs you" is as useful an answer as a list.
function lede(queue, messages, office) {
  const bits = [];
  if (queue.length) bits.push(`${queue.length} thing${queue.length === 1 ? '' : 's'} need${queue.length === 1 ? 's' : ''} ${office ? 'the team' : 'you'} today`);
  if (messages.length) bits.push(`${messages.length} new repl${messages.length === 1 ? 'y' : 'ies'}`);
  const closing = queue.find(q => q.kind === 'closing');
  if (closing) bits.push(`next closing ${closing.when.toLowerCase()}`);
  return bits.length ? bits.join(' · ') : 'Nothing urgent — a good day to go find one.';
}

function QueueRow({ item, queue, tz, onDone, onOpen }) {
  const sev = SEV[item.severity] || SEV.info;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '4px 1fr auto', gap: '0 13px',
                  alignItems: 'start', padding: '12px 17px 12px 0', borderBottom: '1px solid var(--navy-border)' }}>
      <div style={{ alignSelf: 'stretch', borderRadius: '0 3px 3px 0', background: sev.stripe }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
          {item.title}{item.address ? <> — <span style={{ color: 'var(--teal)' }}>{item.address}</span></> : null}
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3, lineHeight: 1.45 }}>{item.detail}</div>
        {item.latest_note && (
          <div style={{ fontSize: 11.5, color: 'var(--gray-300)', marginTop: 5, lineHeight: 1.45, paddingLeft: 8,
                        borderLeft: '2px solid var(--navy-border)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {String(item.latest_note.body).slice(0, 200)}{String(item.latest_note.body).length > 200 ? '…' : ''}
            {item.latest_note.author ? <span style={{ color: 'var(--gray-500)' }}> — {item.latest_note.author}</span> : null}
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <QueueItemActions item={item} queue={queue} tz={tz} onDone={onDone} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                      whiteSpace: 'nowrap', color: sev.when }}>{item.when}</div>
        <button onClick={onOpen} style={{ ...act, ...(item.severity === 'crit' ? actPrimary : null) }}>
          {item.count > 1 ? 'View deals' : 'Open deal'}
        </button>
      </div>
    </div>
  );
}

function Funnel({ rows, onPick }) {
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div style={{ padding: '15px 17px 17px', display: 'flex', flexDirection: 'column', gap: 9 }}>
      {rows.map(r => (
        <button key={r.stage} onClick={onPick}
          style={{ display: 'grid', gridTemplateColumns: '104px 1fr 34px', gap: 11, alignItems: 'center',
                   background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
          <span style={{ fontSize: 12, color: 'var(--gray-300)' }}>{r.stage}</span>
          <span style={{ height: 15, borderRadius: 4, background: 'var(--navy-light)', overflow: 'hidden' }}>
            <i style={{ display: 'block', height: '100%', borderRadius: 4,
                        width: `${Math.round((r.count / max) * 100)}%`,
                        background: r.stage === 'Offer Sent' ? 'var(--gold)' : 'var(--teal)', opacity: 0.85 }} />
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
        </button>
      ))}
    </div>
  );
}

const DOT = { closing: 'var(--teal)', inspection: 'var(--red)', followup: 'var(--gold)' };
const KIND_LABEL = { closing: 'Closing', inspection: 'Inspection ends', followup: 'Follow-up due' };

function Calendar({ events, onPick }) {
  const today = new Date();
  const [month, setMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const byDay = useMemo(() => {
    const m = {};
    for (const e of events) {
      const key = ymd(e.date);
      if (!key) continue;
      (m[key] = m[key] || []).push(e);
    }
    return m;
  }, [events]);

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysIn = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(first.getDay()).fill(null),
                 ...Array.from({ length: daysIn }, (_, i) => i + 1)];
  const todayKey = ymd(today);
  const key = (d) => `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const upcoming = events.slice(0, 6);

  return (
    <>
      <div style={{ padding: '13px 15px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{MONTHS[month.getMonth()]} {month.getFullYear()}</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button aria-label="Previous month" style={navBtn}
              onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>‹</button>
            <button aria-label="Next month" style={navBtn}
              onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>›</button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--gray-500)', textAlign: 'center', paddingBottom: 3 }}>{d}</div>
          ))}
          {cells.map((d, i) => {
            if (d == null) return <div key={`b${i}`} />;
            const k = key(d);
            const evs = byDay[k] || [];
            const isToday = k === todayKey;
            return (
              <div key={k} title={evs.map(e => `${KIND_LABEL[e.kind]}: ${e.address}`).join('\n') || undefined}
                style={{
                  aspectRatio: '1 / 0.92', borderRadius: 7, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 3, fontSize: 11.5,
                  fontVariantNumeric: 'tabular-nums',
                  background: isToday ? 'var(--teal)' : evs.length ? 'var(--navy-light)' : 'transparent',
                  color: isToday ? '#fff' : evs.length ? 'var(--white)' : 'var(--gray-300)',
                  fontWeight: isToday || evs.length ? 700 : 400,
                }}>
                {d}
                <span style={{ display: 'flex', gap: 2, height: 4 }}>
                  {[...new Set(evs.map(e => e.kind))].slice(0, 3).map(kind => (
                    <i key={kind} style={{ width: 4, height: 4, borderRadius: '50%', background: DOT[kind],
                                           boxShadow: isToday ? '0 0 0 1px rgba(255,255,255,0.6)' : 'none' }} />
                  ))}
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 11, padding: '11px 2px 12px', fontSize: 10.5, color: 'var(--gray-500)' }}>
          {Object.entries(KIND_LABEL).map(([k2, label]) => (
            <span key={k2} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <i style={{ width: 6, height: 6, borderRadius: '50%', background: DOT[k2] }} />{label}
            </span>
          ))}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--navy-border)' }}>
        {upcoming.length === 0 ? (
          <Empty>Nothing on the calendar in the next six weeks.</Empty>
        ) : upcoming.map((e, i) => {
          const p = dayParts(e.date);
          return (
            <button key={`${e.kind}-${e.deal_id}-${i}`} onClick={() => e.deal_id && onPick(e.deal_id)}
              style={{ display: 'grid', gridTemplateColumns: '46px 1fr auto', gap: 11, alignItems: 'center',
                       padding: '10px 17px', borderBottom: '1px solid var(--navy-border)', width: '100%',
                       background: 'none', border: 0, borderBottomWidth: 1, borderBottomStyle: 'solid',
                       cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
              <span style={{ textAlign: 'center' }}>
                <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase' }}>
                  {p ? p.short : ''}
                </span>
                <span style={{ display: 'block', fontSize: 16, fontWeight: 700, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>
                  {p ? p.d : ''}
                </span>
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.address}
                </span>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--gray-500)' }}>
                  {KIND_LABEL[e.kind]}{e.fee ? ` · ${money(Number(e.fee))} fee` : ''}
                </span>
              </span>
              <span style={{ ...tag, background: 'var(--navy-light)', color: 'var(--gray-300)' }}>
                {e.kind === 'closing' ? 'Closing' : e.kind === 'inspection' ? 'Option' : 'Follow-up'}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

// Earnest money — the agent's own cash at risk. Three buckets, and the two that still hold money name
// the deals behind them so a number isn't something you then have to go hunting for.
//
// NOTHING here is inferred. Every figure is a status a person recorded on a deal; the app's job is to
// ASK at the right moment (the queue prompts when an option period is ending, or a deal sells or
// dies) and then report the answer faithfully. A status the app decided would read as a fact the
// agent had confirmed, which for money is the wrong way round.
function EarnestMoney({ emd, onPick, office }) {
  const { out, pending, lost } = emd;
  const nothing = !out.count && !pending.count && !lost.count;
  const rows = [
    { key: 'out',     label: office ? 'Sent — office' : 'Sent (out at title)', v: out,     tone: 'var(--teal)' },
    { key: 'pending', label: 'Pending',                                        v: pending, tone: 'var(--gold)' },
    { key: 'lost',    label: 'Lost',                                           v: lost,    tone: 'var(--red)' },
  ];
  return (
    <div>
      <div style={{ padding: '15px 17px 4px' }}>
        <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
          {money(out.amount)}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2 }}>
          {out.count ? `at risk across ${out.count} deal${out.count === 1 ? '' : 's'}` : 'nothing at risk right now'}
        </div>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 5, fontSize: 11.5, color: 'var(--gray-300)' }}>
          {rows.map(r => (
            <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: r.tone }} />
              {r.label}{r.v.count ? ` (${r.v.count})` : ''}
              <b style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--white)' }}>{money(r.v.amount)}</b>
            </div>
          ))}
        </div>
        {nothing && (
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 10, lineHeight: 1.5 }}>
            Nothing recorded yet. Set the EMD status on a deal and it appears here — the app never
            changes it for you, it only asks when an option period is ending or a deal sells or dies.
          </div>
        )}
      </div>
      {emd.deals.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--navy-border)' }}>
          {emd.deals.slice(0, 5).map(d => (
            <button key={d.id} onClick={() => onPick(d.id)} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 17px', width: '100%',
              borderBottom: '1px solid var(--navy-border)', background: 'none', border: 0,
              borderBottomWidth: 1, borderBottomStyle: 'solid', cursor: 'pointer', fontFamily: 'inherit',
            }}>
              <span style={{ ...tag, background: d.bucket === 'pending' ? 'var(--gold-dim)' : 'var(--teal-dim)',
                             color: d.bucket === 'pending' ? '#8a6314' : 'var(--teal)' }}>
                {d.bucket === 'pending' ? 'Pending' : d.stage}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, textAlign: 'left',
                             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.address}</span>
              <span style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {money(d.earnest_money)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Small shared bits ─────────────────────────────────────────────────────────
const Centered = ({ children, tone }) => (
  <div style={{ padding: 60, textAlign: 'center', fontSize: 14, color: tone === 'bad' ? 'var(--red)' : 'var(--gray-500)' }}>{children}</div>
);
const Empty = ({ children }) => (
  <div style={{ padding: '26px 20px', textAlign: 'center', color: 'var(--gray-300)', fontSize: 13.5 }}>{children}</div>
);
const Hint = ({ children }) => (
  <div style={{ fontSize: 11.5, color: 'var(--gray-500)', padding: '11px 17px', borderTop: '1px solid var(--navy-border)', background: 'var(--navy-light)' }}>{children}</div>
);
const SegBtn = ({ on, onClick, children }) => (
  <button onClick={onClick} aria-pressed={on} style={{
    font: 'inherit', fontSize: 12.5, fontWeight: 600, border: 0, borderRadius: 6, padding: '6px 14px',
    cursor: 'pointer', background: on ? 'var(--teal)' : 'transparent', color: on ? '#fff' : 'var(--gray-500)',
  }}>{children}</button>
);

function Panel({ title, count, hot, link, onLink, children }) {
  return (
    <section style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, overflow: 'hidden' }}>
      <h2 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em',
                   color: 'var(--gray-300)', margin: 0, padding: '14px 17px 12px', display: 'flex',
                   alignItems: 'center', gap: 9, borderBottom: '1px solid var(--navy-border)' }}>
        {title}
        {count != null && count !== 0 && (
          <span style={{ borderRadius: 999, fontSize: 11, padding: '1px 8px', letterSpacing: 0, fontVariantNumeric: 'tabular-nums',
                         background: hot ? 'var(--red-dim, rgba(184,84,80,0.12))' : 'var(--navy-light)',
                         color: hot ? 'var(--red)' : 'var(--gray-300)' }}>{count}</span>
        )}
        {link && (
          <button onClick={onLink} style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 600, letterSpacing: 0,
                                            textTransform: 'none', color: 'var(--teal)', background: 'none',
                                            border: 0, cursor: 'pointer', fontFamily: 'inherit' }}>{link}</button>
        )}
      </h2>
      {children}
    </section>
  );
}

function Kpi({ label, value, sub, feature }) {
  return (
    <div style={{ background: feature ? 'var(--teal)' : 'var(--navy-card)',
                  border: `1px solid ${feature ? 'var(--teal)' : 'var(--navy-border)'}`,
                  borderRadius: 12, padding: '14px 16px 15px', minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                    color: feature ? 'rgba(244,241,232,0.72)' : 'var(--gray-500)' }}>{label}</div>
      <div style={{ fontSize: 27, fontWeight: 700, lineHeight: 1.12, marginTop: 8, letterSpacing: '-0.02em',
                    fontVariantNumeric: 'tabular-nums', color: feature ? '#F4F1E8' : 'var(--white)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, marginTop: 3, color: feature ? 'var(--feebe-sage, #7FBCA9)' : 'var(--gray-500)' }}>{sub}</div>}
    </div>
  );
}

function initials(name) {
  const parts = String(name || '?').trim().split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
}
function ago(ts) {
  if (!ts) return '';
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const seg = { display: 'inline-flex', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 9, padding: 3, gap: 3 };
const pill = { font: 'inherit', fontSize: 11.5, fontWeight: 600, padding: '4px 11px', borderRadius: 999, border: '1px solid var(--navy-border)', cursor: 'pointer' };
const kpiGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 18 };
const board = { display: 'grid', gridTemplateColumns: 'minmax(0, 1.62fr) minmax(0, 1fr)', gap: 18, alignItems: 'start' };
const col = { display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 };
const act = { fontSize: 11.5, fontWeight: 600, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', borderRadius: 7, padding: '4px 10px', fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' };
const actPrimary = { background: 'var(--teal)', borderColor: 'var(--teal)', color: '#fff' };
const msgRow = { display: 'grid', gridTemplateColumns: '30px 1fr auto', gap: 11, padding: '12px 17px', borderBottom: '1px solid var(--navy-border)', alignItems: 'start', width: '100%', background: 'none', border: 0, borderBottomWidth: 1, borderBottomStyle: 'solid', cursor: 'pointer', fontFamily: 'inherit' };
const avatar = { width: 30, height: 30, borderRadius: '50%', background: 'var(--teal)', color: '#FAF7F2', fontSize: 11, fontWeight: 700, display: 'grid', placeItems: 'center' };
const navBtn = { width: 22, height: 22, borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-500)', fontFamily: 'inherit', fontSize: 11, lineHeight: 1, cursor: 'pointer' };
const tag = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap', flexShrink: 0 };
const alertRow = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 17px', borderBottom: '1px solid var(--navy-border)' };
const alertAddr = { flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' };
const alertPrice = { fontSize: 12, color: 'var(--gray-500)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const claimRow = { display: 'flex', alignItems: 'center', gap: 11, padding: '11px 17px', borderBottom: '1px solid var(--navy-border)' };
